
import React, { useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { DeviceProfileFormProps } from './types';
import { useDeviceForm } from './useDeviceForm';
import { useFormNavigation } from './hooks/useFormNavigation';
import FormStepIndicator from './FormStepIndicator';
import SuccessDialog from './SuccessDialog';
import ConfirmCancelDialog from './ConfirmCancelDialog';
import FormStepContent from './components/FormStepContent';
import FormNavigationButtons from './components/FormNavigationButtons';

const DeviceProfileForm: React.FC<DeviceProfileFormProps> = ({ onComplete }) => {
  const { 
    formData,
    handleChange,
    validateStep,
    isSubmitting,
    setIsSubmitting,
    showSuccessDialog,
    setShowSuccessDialog,
    handleComplete,
    availableProtocols,
    errors
  } = useDeviceForm(onComplete);
  
  const { toast } = useToast();
  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);
  const [pendingAction, setPendingAction] = React.useState<() => void>(() => () => {});

  const { 
    step, 
    setStep,
    handleNext, 
    handleBack, 
    isFirstStep, 
    isLastStep 
  } = useFormNavigation({
    validateStep,
    totalSteps: 4
  });

  useEffect(() => {
    const hasFormData = Object.values(formData).some(value => value.trim() !== '');
    
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasFormData && !showSuccessDialog) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [formData, showSuccessDialog]);

  const handleCancel = () => {
    if (Object.values(formData).some(value => value.trim() !== '')) {
      setShowConfirmDialog(true);
      setPendingAction(() => onComplete);
    } else {
      onComplete();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateStep(step)) {
      toast({
        title: "Validation Error",
        description: `Please fill in all required fields in step ${step}`,
        variant: "destructive"
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Simulate API request to register device
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setShowSuccessDialog(true);
    } catch (error) {
      toast({
        title: "Registration Failed",
        description: "There was an error registering the device. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const stepTitles = [
    { title: "Device Profile Details" },
    { title: "Transport Configuration", optional: true },
    { title: "Alarm Rule", optional: true },
    { title: "Device Provisioning", optional: true },
  ];

  return (
    <>
      <FormStepIndicator 
        currentStep={step} 
        totalSteps={4} 
        stepTitles={stepTitles} 
      />

      <form onSubmit={handleSubmit} className="space-y-6">
        <FormStepContent
          step={step}
          formData={formData}
          handleChange={handleChange}
          availableProtocols={availableProtocols}
          errors={errors}
        />
        
        <FormNavigationButtons
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
          isSubmitting={isSubmitting}
          handleBack={handleBack}
          handleNext={handleNext}
          handleCancel={handleCancel}
          validateStep={validateStep}
          currentStep={step}
        />
      </form>

      <SuccessDialog 
        open={showSuccessDialog} 
        setOpen={setShowSuccessDialog}
        deviceName={formData.name}
        onComplete={handleComplete}
      />

      <ConfirmCancelDialog
        open={showConfirmDialog}
        setOpen={setShowConfirmDialog}
        onConfirm={() => {
          pendingAction();
          setShowConfirmDialog(false);
        }}
      />
    </>
  );
};

export default DeviceProfileForm;
